import React from 'react';
import { Switch, Typography, Tag, theme } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconUserCog } from '@tabler/icons-react';
import EmptyState from './EmptyState';
import { IOperator } from '../interfaces/heavyDutyMachinery/IOperator';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { Text } = Typography;

interface OperatorAssignmentPickerProps {
  operators: IOperator[];
  loading?: boolean;
  // Optional here even though this is always used inside a Form.Item, which
  // injects both at clone time -- same convention as WizardTileGrid.
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  /**
   * The machine's own currently-selected Category -- used ONLY to sort
   * qualified operators first and show a "Cualificado" badge, never to hide
   * anyone. Per the host's own explicit decision (see MachineryOperatorLink's
   * doc comment, API repo), QualifiedCategories is a suggestion at
   * assignment time, not a hard filter -- the host may still assign an
   * operator outside their usual category if they know better.
   */
  qualifiedCategory?: string;
}

/**
 * Multi-toggle roster picker for "which of my own operators may run THIS
 * machine" -- the assignment step that used to not exist at all (see this
 * feature's own doc entry): a guest used to be offered any operator merely
 * qualified for the machine's Category, now only operators the host
 * explicitly turns on here. Deliberately a plain controlled Form.Item value
 * (string[] of Operator ids) rather than MachineryAccessoryLinkSection's own
 * immediate-effect API-linked pattern -- this has to work during the create
 * wizard's own "Operario" phase, before a real Machinery.Id exists to link
 * against, so the assignment is staged in wizard state like every other
 * field and only sent to the backend at submit time.
 */
const OperatorAssignmentPicker: React.FC<OperatorAssignmentPickerProps> = ({
  operators,
  loading,
  value,
  onChange,
  disabled,
  qualifiedCategory,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const selected = new Set(value ?? []);

  const isQualified = (operator: IOperator) =>
    !!qualifiedCategory &&
    operator.qualifiedCategories.split(',').map((c) => c.trim()).filter(Boolean).includes(qualifiedCategory);

  const activeOperators = operators
    .filter((o) => o.isActive)
    .slice()
    .sort((a, b) => Number(isQualified(b)) - Number(isQualified(a)));

  const toggle = (operatorId: string, checked: boolean) => {
    if (disabled) return;
    const next = new Set(selected);
    if (checked) next.add(operatorId);
    else next.delete(operatorId);
    onChange?.(Array.from(next));
  };

  if (loading) return null;

  if (activeOperators.length === 0) {
    return (
      <EmptyState
        icon={<IconUserCog size={20} />}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_ASSIGNMENT_EMPTY_TITLE)}
        description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_ASSIGNMENT_EMPTY_DESCRIPTION)}
        action={
          <Link to="/heavy-duty-machinery/operators" target="_blank" rel="noopener noreferrer">
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_ASSIGNMENT_EMPTY_CTA)}
          </Link>
        }
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {activeOperators.map((operator) => (
        <div
          key={operator.id}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            borderRadius: 14,
            background: token.colorBgContainer,
            border: `1px solid ${selected.has(operator.id) ? token.colorPrimary : token.colorBorderSecondary}`,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text strong style={{ display: 'block' }}>{operator.fullName}</Text>
              {isQualified(operator) && (
                <Tag color="success" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '18px' }}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_ASSIGNMENT_QUALIFIED_BADGE)}
                </Tag>
              )}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>{operator.hourlyRate.toFixed(2)}€/h</Text>
          </div>
          <Switch
            checked={selected.has(operator.id)}
            disabled={disabled}
            onChange={(checked) => toggle(operator.id, checked)}
          />
        </div>
      ))}
    </div>
  );
};

export default OperatorAssignmentPicker;
